import {
  Injectable,
  OnDestroy,
  Renderer2,
  RendererFactory2
} from '@angular/core';
import { getLocaleDirection } from '@angular/common';
import { environment } from '../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { DarkThemeService } from './dark-theme.service';
import { LOCAL_STORAGE_KEYS } from '../constants/storage-keys';
import { isSupportedLocale } from '../constants/locales';
import { getLanguageWithFallback } from '../utils/storage-utils';

interface IframeMessage {
  type: string;
  data: any;
}

/**
 * Configuration for iframe height behavior.
 * All properties are optional - if not set, auto-calculation is used.
 */
export interface IframeHeightConfig {
  /** Fixed height in pixels. When set, overrides auto-calculation. */
  fixedHeight?: number;
  /** Minimum height constraint in pixels. */
  minHeight?: number;
  /** Maximum height constraint in pixels. */
  maxHeight?: number;
  /** Offset/padding to add to calculated height (can be negative). */
  heightOffset?: number;
}

/**
 * Service for handling iframe communication in Colombia application.
 *
 * Supported iframe events (inbound - parent to iframe):
 * - fontSizeMultiplier: Set font size multiplier (data: number)
 * - darkMode: Set dark mode (data: boolean)
 * - currentUrl: Get current URL (no data required)
 * - requestResize: Request iframe resize (no data required)
 * - getCurrentLanguage: Get current language (no data required)
 *   - Response: 'currentLanguage' event with language code string
 * - setLanguage: Set language (data: string - one of 'et', 'fr', 'en', 'es', 'ar', 'pt')
 *   - Response: 'languageSet' event with the set language code
 *   - Note: This will reload the page to apply the new language
 *
 * Height configuration events (inbound):
 * - setHeightConfig: Set full height configuration (data: IframeHeightConfig)
 *   - Response: 'heightConfigReceived' event with the applied config
 * - setFixedHeight: Set or clear fixed height (data: number | null)
 *   - Response: 'heightConfigReceived' event with the applied config
 * - setHeightConstraints: Set min/max constraints (data: {minHeight?: number, maxHeight?: number})
 *   - Response: 'heightConfigReceived' event with the applied config
 * - setHeightOffset: Set height offset/padding (data: number)
 *   - Response: 'heightConfigReceived' event with the applied config
 * - clearHeightConfig: Reset to auto-calculate mode (no data required)
 *   - Response: 'heightConfigReceived' event with empty config
 *
 * Outbound events (iframe to parent):
 * - ready: Sent when service is initialized
 * - resize: Sent with {height: number} when iframe height changes
 * - heightConfigReceived: Confirms height config was applied
 * - error: Reports invalid config values {type: string, message: string}
 */
@Injectable({
  providedIn: 'root'
})
export class ColombiaIframeService implements OnDestroy {
  private readonly renderer: Renderer2;
  private messageListener: any;
  private parentOrigin: string = '*';
  private readonly isInIframe: boolean;
  private resizeIntervalId: number | null = null;

  public fontSizeMultiplier$ = new BehaviorSubject<number>(1);
  private heightConfig$ = new BehaviorSubject<IframeHeightConfig>({});

  constructor(
    rendererFactory: RendererFactory2,
    private readonly darkThemeService: DarkThemeService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    this.isInIframe = window.parent !== window.self;

    if (environment.IS_IFRAME_APP) {
      this.initMessageListener();
      this.initAutoResize();
      setTimeout(() => {
        this.sendMessageToParent('ready', true);
      }, 0);
    }
  }

  private initMessageListener(): void {
    this.messageListener = this.renderer.listen(
      'window',
      'message',
      (event: MessageEvent) => {
        if (!this.isOriginAllowed(event.origin)) {
          console.warn(`Message from disallowed origin: ${event.origin}`);
          return;
        }

        try {
          const message = event.data as IframeMessage;

          if (!message?.type) {
            return;
          }

          this.parentOrigin = event.origin;

          switch (message.type) {
            case 'fontSizeMultiplier':
              this.handleFontSizeMultiplierMessage(message.data);
              break;
            case 'darkMode':
              this.handleDarkModeMessage(message.data);
              break;
            case 'currentUrl':
              this.handleCurrentUrlMessage();
              break;
            case 'requestResize':
              this.sendHeightToParent();
              break;
            case 'getCurrentLanguage':
              this.handleGetCurrentLanguageMessage();
              break;
            case 'setLanguage':
              this.handleSetLanguageMessage(message.data);
              break;
            case 'setHeightConfig':
              this.handleSetHeightConfigMessage(message.data);
              break;
            case 'setFixedHeight':
              this.handleSetFixedHeightMessage(message.data);
              break;
            case 'setHeightConstraints':
              this.handleSetHeightConstraintsMessage(message.data);
              break;
            case 'setHeightOffset':
              this.handleSetHeightOffsetMessage(message.data);
              break;
            case 'clearHeightConfig':
              this.handleClearHeightConfigMessage();
              break;
            default:
              console.warn(`Unknown message type: ${message.type}`);
          }
        } catch (error) {
          console.error('Error processing iframe message:', error);
        }
      }
    );
  }

  private initAutoResize(): void {
    if (!this.isInIframe) {
      return; // Only initialize if we're in an iframe
    }

    // Send height on load and whenever window resizes
    this.renderer.listen('window', 'load', () => this.sendHeightToParent());
    this.renderer.listen('window', 'resize', () => this.sendHeightToParent());

    // Also send height periodically to handle dynamic content changes
    this.resizeIntervalId = window.setInterval(
      () => this.sendHeightToParent(),
      500
    );
  }

  private sendHeightToParent(): void {
    const config = this.heightConfig$.value;
    let height: number;

    if (config.fixedHeight !== undefined && config.fixedHeight > 0) {
      // Use fixed height if set
      height = config.fixedHeight;
    } else {
      // Calculate from scrollHeight
      height =
        document.body.scrollHeight || document.documentElement.scrollHeight;

      // Apply offset
      if (config.heightOffset) {
        height += config.heightOffset;
      }
    }

    // Apply constraints
    if (config.minHeight !== undefined && height < config.minHeight) {
      height = config.minHeight;
    }
    if (config.maxHeight !== undefined && height > config.maxHeight) {
      height = config.maxHeight;
    }

    this.sendMessageToParent('resize', { height });
  }

  private isOriginAllowed(origin: string): boolean {
    if (
      !environment.ALLOWED_ORIGINS ||
      environment.ALLOWED_ORIGINS.length === 0
    ) {
      return false;
    }

    if (environment.ALLOWED_ORIGINS.includes('*')) return true;

    return environment.ALLOWED_ORIGINS.includes(origin);
  }

  sendMessageToParent(type: string, data: any): void {
    const message = { type, data };
    window.parent.postMessage(message, this.parentOrigin);
  }

  private handleFontSizeMultiplierMessage(multiplier: any): void {
    if (typeof multiplier === 'number') {
      this.fontSizeMultiplier$.next(multiplier);
      this.applyFontSizeMultiplier(multiplier);
      this.sendMessageToParent('fontSizeMultiplierReceived', true);
    } else {
      this.sendMessageToParent('error', {
        type: 'fontSizeMultiplier',
        message: 'Invalid value'
      });
    }
  }

  private handleDarkModeMessage(isDarkMode: any): void {
    if (typeof isDarkMode === 'boolean') {
      this.darkThemeService.setDarkMode(isDarkMode);
      this.sendMessageToParent('darkModeReceived', true);
    } else {
      this.sendMessageToParent('error', {
        type: 'darkMode',
        message: 'Invalid value'
      });
    }
  }

  private handleCurrentUrlMessage(): void {
    this.sendMessageToParent('currentUrl', window.location);
  }

  private handleGetCurrentLanguageMessage(): void {
    const currentLanguage = getLanguageWithFallback();
    this.sendMessageToParent('currentLanguage', currentLanguage);
  }

  private handleSetLanguageMessage(language: any): void {
    if (!isSupportedLocale(language)) {
      this.sendMessageToParent('error', {
        type: 'setLanguage',
        message: 'Invalid or unsupported language'
      });
      return;
    }

    // Set the language in localStorage
    localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, language);

    // Update document properties
    document.documentElement.lang = language;
    document.documentElement.dir = getLocaleDirection(language);

    // Notify parent that language was set successfully
    this.sendMessageToParent('languageSet', language);
    window.location.reload();
  }

  private handleSetHeightConfigMessage(config: any): void {
    const validationError = this.validateHeightConfig(config);
    if (validationError) {
      this.sendMessageToParent('error', {
        type: 'setHeightConfig',
        message: validationError
      });
      return;
    }

    const newConfig: IframeHeightConfig = {
      fixedHeight: config.fixedHeight,
      minHeight: config.minHeight,
      maxHeight: config.maxHeight,
      heightOffset: config.heightOffset
    };

    this.heightConfig$.next(newConfig);
    this.sendMessageToParent('heightConfigReceived', newConfig);
    this.sendHeightToParent();
  }

  private handleSetFixedHeightMessage(height: any): void {
    if (height !== null && (typeof height !== 'number' || height <= 0)) {
      this.sendMessageToParent('error', {
        type: 'setFixedHeight',
        message: 'Fixed height must be a positive number or null'
      });
      return;
    }

    const currentConfig = this.heightConfig$.value;
    const newConfig: IframeHeightConfig = {
      ...currentConfig,
      fixedHeight: height === null ? undefined : height
    };

    this.heightConfig$.next(newConfig);
    this.sendMessageToParent('heightConfigReceived', newConfig);
    this.sendHeightToParent();
  }

  private handleSetHeightConstraintsMessage(constraints: any): void {
    if (typeof constraints !== 'object' || constraints === null) {
      this.sendMessageToParent('error', {
        type: 'setHeightConstraints',
        message: 'Constraints must be an object'
      });
      return;
    }

    const { minHeight, maxHeight } = constraints;

    if (
      minHeight !== undefined &&
      (typeof minHeight !== 'number' || minHeight < 0)
    ) {
      this.sendMessageToParent('error', {
        type: 'setHeightConstraints',
        message: 'minHeight must be a non-negative number'
      });
      return;
    }

    if (
      maxHeight !== undefined &&
      (typeof maxHeight !== 'number' || maxHeight <= 0)
    ) {
      this.sendMessageToParent('error', {
        type: 'setHeightConstraints',
        message: 'maxHeight must be a positive number'
      });
      return;
    }

    if (
      minHeight !== undefined &&
      maxHeight !== undefined &&
      minHeight > maxHeight
    ) {
      this.sendMessageToParent('error', {
        type: 'setHeightConstraints',
        message: 'minHeight cannot be greater than maxHeight'
      });
      return;
    }

    const currentConfig = this.heightConfig$.value;
    const newConfig: IframeHeightConfig = {
      ...currentConfig,
      minHeight,
      maxHeight
    };

    this.heightConfig$.next(newConfig);
    this.sendMessageToParent('heightConfigReceived', newConfig);
    this.sendHeightToParent();
  }

  private handleSetHeightOffsetMessage(offset: any): void {
    if (typeof offset !== 'number') {
      this.sendMessageToParent('error', {
        type: 'setHeightOffset',
        message: 'Offset must be a number'
      });
      return;
    }

    const currentConfig = this.heightConfig$.value;
    const newConfig: IframeHeightConfig = {
      ...currentConfig,
      heightOffset: offset
    };

    this.heightConfig$.next(newConfig);
    this.sendMessageToParent('heightConfigReceived', newConfig);
    this.sendHeightToParent();
  }

  private handleClearHeightConfigMessage(): void {
    this.heightConfig$.next({});
    this.sendMessageToParent('heightConfigReceived', {});
    this.sendHeightToParent();
  }

  private validateHeightConfig(config: any): string | null {
    if (typeof config !== 'object' || config === null) {
      return 'Config must be an object';
    }

    if (
      config.fixedHeight !== undefined &&
      (typeof config.fixedHeight !== 'number' || config.fixedHeight <= 0)
    ) {
      return 'fixedHeight must be a positive number';
    }

    if (
      config.minHeight !== undefined &&
      (typeof config.minHeight !== 'number' || config.minHeight < 0)
    ) {
      return 'minHeight must be a non-negative number';
    }

    if (
      config.maxHeight !== undefined &&
      (typeof config.maxHeight !== 'number' || config.maxHeight <= 0)
    ) {
      return 'maxHeight must be a positive number';
    }

    if (
      config.minHeight !== undefined &&
      config.maxHeight !== undefined &&
      config.minHeight > config.maxHeight
    ) {
      return 'minHeight cannot be greater than maxHeight';
    }

    if (
      config.heightOffset !== undefined &&
      typeof config.heightOffset !== 'number'
    ) {
      return 'heightOffset must be a number';
    }

    return null;
  }

  private applyFontSizeMultiplier(multiplier: number): void {
    let styleEl = document.head.querySelector('style#font-accessibility-style');

    if (!styleEl) {
      styleEl = this.renderer.createElement('style');
      this.renderer.setAttribute(styleEl, 'id', 'font-accessibility-style');
      this.renderer.appendChild(document.head, styleEl);
    }

    const cssContent = `:root { --font-accessibility-value: ${multiplier}px; }`;
    this.renderer.setProperty(styleEl, 'textContent', cssContent);
  }

  public isRunningInIframe(): boolean {
    return environment.IS_IFRAME_APP && this.isInIframe;
  }

  ngOnDestroy(): void {
    if (this.messageListener) {
      this.messageListener();
    }

    if (this.resizeIntervalId !== null) {
      window.clearInterval(this.resizeIntervalId);
      this.resizeIntervalId = null;
    }
  }
}
